import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Trash2 } from 'lucide-react';
import './AdsConfig.css'; // reuse existing config styles

import { fetchCacheConfigs, updateCacheConfig, clearCache } from '../services/api.ts';

interface CacheFeature {
  id: number;
  feature_key: string;
  cache_enabled: boolean;
  cache_ttl_minutes: number;
  updated_at: string | null;
}

const FEATURE_LABELS: Record<string, { title: string; description: string }> = {
  home_products: {
    title: 'Home Page Products',
    description: 'Cache featured products per country on the home screen.',
  },
};

const CacheConfig: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [clearing, setClearing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [features, setFeatures] = useState<CacheFeature[]>([]);

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCacheConfigs();
      setFeatures(data);
    } catch {
      setError('Failed to load cache configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfigs(); }, []);

  const handleToggle = (key: string, value: boolean) => {
    setFeatures(prev =>
      prev.map(f => f.feature_key === key ? { ...f, cache_enabled: value } : f)
    );
  };

  const handleTtlChange = (key: string, value: number) => {
    setFeatures(prev =>
      prev.map(f => f.feature_key === key ? { ...f, cache_ttl_minutes: value } : f)
    );
  };

  const handleSave = async (feature: CacheFeature) => {
    setSaving(feature.feature_key);
    setError(null);
    setSuccess(null);
    try {
      await updateCacheConfig(feature.feature_key, {
        cache_enabled: feature.cache_enabled,
        cache_ttl_minutes: feature.cache_ttl_minutes,
      });
      setSuccess(`"${FEATURE_LABELS[feature.feature_key]?.title || feature.feature_key}" saved.`);
      loadConfigs();
    } catch {
      setError('Failed to save configuration.');
    } finally {
      setSaving(null);
    }
  };

  const handleClear = async (featureKey: string) => {
    setClearing(featureKey);
    setError(null);
    setSuccess(null);
    try {
      const result = await clearCache(featureKey);
      setSuccess(`Cache cleared. ${result.keys_deleted} key(s) removed.`);
    } catch {
      setError('Failed to clear cache.');
    } finally {
      setClearing(null);
    }
  };

  if (loading) {
    return <div className="ads-config-page"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="ads-config-page">
      <div className="page-header">
        <h1>Cache Configuration</h1>
        <button className="btn-refresh" onClick={loadConfigs} title="Refresh">
          <RefreshCw size={18} />
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {features.map(feature => {
        const label = FEATURE_LABELS[feature.feature_key] || {
          title: feature.feature_key,
          description: '',
        };

        return (
          <div className="config-card" key={feature.feature_key} style={{ marginBottom: 20 }}>
            <div className="config-row">
              <div className="config-label">
                <h3>{label.title}</h3>
                <p>{label.description}</p>
              </div>
              <div className="config-control">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={feature.cache_enabled}
                    onChange={e => handleToggle(feature.feature_key, e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className={`toggle-label ${feature.cache_enabled ? 'on' : 'off'}`}>
                  {feature.cache_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>

            <div className="config-row">
              <div className="config-label">
                <h3>Cache Duration</h3>
                <p>How long cached data stays valid before refreshing from the database.</p>
              </div>
              <div className="config-control">
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={feature.cache_ttl_minutes}
                  onChange={e => handleTtlChange(feature.feature_key, Number(e.target.value))}
                  className="config-input"
                />
                <span className="input-hint">minutes</span>
              </div>
            </div>

            <div className="config-footer">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {feature.updated_at && (
                  <span className="last-updated">
                    Last updated: {new Date(feature.updated_at).toLocaleString()}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn-refresh"
                  onClick={() => handleClear(feature.feature_key)}
                  disabled={clearing === feature.feature_key}
                  title="Clear cached data now"
                  style={{ padding: '8px 14px', fontSize: '0.85rem', gap: 4, display: 'flex', alignItems: 'center' }}
                >
                  <Trash2 size={15} />
                  {clearing === feature.feature_key ? 'Clearing...' : 'Clear Cache'}
                </button>
                <button
                  className="btn-save"
                  onClick={() => handleSave(feature)}
                  disabled={saving === feature.feature_key}
                >
                  <Save size={16} />
                  {saving === feature.feature_key ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CacheConfig;
